import { useState } from 'react';
import type { Match } from '@/lib/site';

interface StatsPanelProps {
  match: Match;
}

export default function StatsPanel({ match }: StatsPanelProps) {
  const [tab, setTab] = useState<'stats' | 'quarters'>('stats');
  const hasScore = match.status !== 'upcoming';

  const homeQuarters = match.home.quarters.length ? match.home.quarters : [0, 0, 0, 0];
  const awayQuarters = match.away.quarters.length ? match.away.quarters : [0, 0, 0, 0];

  return (
    <section aria-labelledby="stats-title" className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-background-200">
        <h2 id="stats-title" className="font-heading text-sm tracking-wider text-foreground-900 flex items-center gap-2">
          <i className="ri-line-chart-line text-primary-600" />
          实时赛况
        </h2>
        <div className="inline-flex items-center gap-1 rounded-full bg-background-100 p-1">
          <button
            type="button"
            onClick={() => setTab('stats')}
            className={`px-3 py-1 rounded-full text-[11px] whitespace-nowrap transition-colors cursor-pointer ${
              tab === 'stats' ? 'bg-primary-900 text-background-50' : 'text-foreground-600'
            }`}
          >
            数据对比
          </button>
          <button
            type="button"
            onClick={() => setTab('quarters')}
            className={`px-3 py-1 rounded-full text-[11px] whitespace-nowrap transition-colors cursor-pointer ${
              tab === 'quarters' ? 'bg-primary-900 text-background-50' : 'text-foreground-600'
            }`}
          >
            分节得分
          </button>
        </div>
      </div>

      {!hasScore ? (
        <div className="px-4 py-8 text-center">
          <i className="ri-timer-line text-2xl text-foreground-400" />
          <p className="mt-2 text-sm text-foreground-700">比赛尚未开始，暂无实时数据</p>
          <p className="mt-1 text-xs text-foreground-500">以下为双方近期场均数据参考</p>
        </div>
      ) : null}

      <div className="px-4 py-4">
        {tab === 'stats' ? (
          <ul className="space-y-3.5">
            {match.stats.map((stat) => (
              <li key={stat.label}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-heading text-sm text-foreground-900">{stat.home}</span>
                  <span className="text-foreground-500">{stat.label}</span>
                  <span className="font-heading text-sm text-foreground-900">{stat.away}</span>
                </div>
                <div className="flex items-center gap-1 h-1.5">
                  <div className="flex-1 h-full rounded-full bg-background-200 overflow-hidden flex justify-end">
                    <div className="h-full rounded-full bg-primary-600" style={{ width: `${stat.homeValue}%` }} />
                  </div>
                  <div className="flex-1 h-full rounded-full bg-background-200 overflow-hidden">
                    <div className="h-full rounded-full bg-accent-500" style={{ width: `${stat.awayValue}%` }} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-foreground-500">
                  <th className="text-left font-normal py-2">球队</th>
                  {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
                    <th key={q} className="text-center font-normal py-2 w-12">
                      {q}
                    </th>
                  ))}
                  <th className="text-right font-normal py-2">总分</th>
                </tr>
              </thead>
              <tbody className="text-foreground-900">
                <tr className="border-t border-background-200">
                  <td className="py-2.5 whitespace-nowrap">{match.home.name}</td>
                  {homeQuarters.map((score, i) => (
                    <td key={`h-${i}`} className="text-center py-2.5">
                      {hasScore ? score : '-'}
                    </td>
                  ))}
                  <td className="text-right py-2.5 font-heading text-sm">{hasScore ? match.home.score : '-'}</td>
                </tr>
                <tr className="border-t border-background-200">
                  <td className="py-2.5 whitespace-nowrap">{match.away.name}</td>
                  {awayQuarters.map((score, i) => (
                    <td key={`a-${i}`} className="text-center py-2.5">
                      {hasScore ? score : '-'}
                    </td>
                  ))}
                  <td className="text-right py-2.5 font-heading text-sm">{hasScore ? match.away.score : '-'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="px-4 py-2.5 bg-background-100 border-t border-background-200">
        <p className="text-[11px] leading-relaxed text-foreground-500">
          <i className="ri-information-line mr-1" />
          以上数据为前端演示内容，未接入官方赛事数据源
        </p>
      </div>
    </section>
  );
}